import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, ViewEncapsulation,
} from '@angular/core';

let nextDialogId = 0;

/**
 * Shared modal dialog shell for the skinned company profiles: an eyebrow, title and short intro over projected content.
 * The parent renders it while open and removes it on (closed). It moves focus to its close button, keeps Tab inside
 * itself, closes on Escape or a backdrop click and returns focus to the element that opened it.
 * size="wide" suits a multi-period table; "medium" suits a list. Skins theme it with --profile-dialog-*.
 */
@Component({
  selector: 'company-profile-dialog',
  templateUrl: './company-profile-dialog.component.html',
  styleUrls: ['./company-profile-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CompanyProfileDialogComponent implements AfterViewInit, OnDestroy {
  @Input() eyebrow = '';
  @Input() heading = '';
  @Input() intro = '';
  @Input() closeLabel = 'Close';
  @Input() size: 'wide' | 'medium' = 'wide';
  @Output() closed = new EventEmitter<void>();
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('dialog') dialog?: ElementRef<HTMLElement>;

  readonly headingId = `profile-dialog-title-${++nextDialogId}`;
  private readonly trigger = typeof document !== 'undefined' ? document.activeElement as HTMLElement | null : null;

  ngAfterViewInit(): void {
    setTimeout(() => this.closeButton?.nativeElement.focus());
  }

  ngOnDestroy(): void {
    const trigger = this.trigger;
    if (trigger && typeof trigger.focus === 'function') setTimeout(() => trigger.focus());
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.closed.emit();
  }

  /** Keep keyboard focus inside the dialog while it is open. */
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.dialog) return;
    const focusable = Array.from(this.dialog.nativeElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    )).filter(element => element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
